/**
 * Edit Chart Tool Schema (XML-first contract, v2)
 *
 * Actions: create | edit | delete | createWaterfall
 * No standalone move action (placement edits happen via XML).
 * readCharts (from readExcel) is the source of truth for edit operations.
 * Uses readId lineage — no etag (per F8).
 */
import { z } from "zod";
const ChartXmlChangeSchema = z.object({
    search: z.string().describe("Exact substring to find in chartXml."),
    replace: z.string().describe("Replacement substring."),
});
const WaterfallStyleSchema = z.object({
    increaseColor: z.string().optional(),
    decreaseColor: z.string().optional(),
    totalColor: z.string().optional(),
});
const WaterfallPlacementSchema = z.object({
    anchorCell: z.string().optional(),
    widthPixels: z.number().optional(),
    heightPixels: z.number().optional(),
});
export const EditChartParamsSchema = z.object({
    uiDescription: z.string()
        .describe("Brief description (max ~15 words) of what this tool call is attempting."),
    sheetName: z.string().describe("Sheet containing the target chart."),
    action: z.enum(["create", "edit", "delete", "createWaterfall"]).describe("create: full chartXml payload. edit: search/replace XML diff. delete: remove chart object. createWaterfall: build waterfall chart from ranges."),
    chartId: z.string().optional().describe("Chart locator ID/name."),
    chartName: z.string().optional().describe("Chart locator alias."),
    chartXml: z.string().optional().describe("Full chart XML payload. Required for create. Optional for edit when forcing a custom base XML."),
    readId: z.string().optional().describe("Optional lineage token from readCharts used to reference the prior canonical read."),
    changes: z.array(ChartXmlChangeSchema).optional().describe("Search/replace XML edits. Required for action='edit'."),
    // createWaterfall inputs
    categoryRange: z.string().optional(),
    valuesRange: z.string().optional(),
    totalPoints: z.array(z.number()).optional(),
    waterfallStyle: WaterfallStyleSchema.optional(),
    title: z.string().optional(),
    numberFormat: z.string().optional(),
    placement: WaterfallPlacementSchema.optional(),
    helperDataRange: z.string().optional(),
    showConnectorLines: z.boolean().optional(),
    showDataLabels: z.boolean().optional(),
    gapWidth: z.number().optional(),
}).superRefine((value, ctx) => {
    const hasLocator = Boolean(value.chartId || value.chartName);
    if (value.action === "create") {
        if (!value.chartXml) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "CREATE requires chartXml.",
                path: ["chartXml"],
            });
        }
        return;
    }
    if (!hasLocator) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `${value.action.toUpperCase()} requires chartId or chartName.`,
            path: ["chartId"],
        });
    }
    if (value.action === "edit" && (!value.changes || value.changes.length === 0)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "EDIT requires non-empty changes[].",
            path: ["changes"],
        });
    }
    if (value.action === "createWaterfall") {
        if (!value.categoryRange) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "createWaterfall requires categoryRange.",
                path: ["categoryRange"],
            });
        }
        if (!value.valuesRange) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "createWaterfall requires valuesRange.",
                path: ["valuesRange"],
            });
        }
    }
});
export { EditChartParamsSchema as EditChartToolParamsSchema };
